import React from 'react';
import './RowValidationCard.scss';

interface BaseRowValidationError {
  row: number;
  issues: string[];
}

interface HomeownerRowValidationError extends BaseRowValidationError {
  email: string;
}

interface CatalogueRowValidationError extends BaseRowValidationError {
  name: string;
}

interface UnitRowValidationError extends BaseRowValidationError {
  unitNumber: string;
}

type RowValidationError =
  | HomeownerRowValidationError
  | CatalogueRowValidationError
  | UnitRowValidationError;

type RowValidationVariant = 'error' | 'warning';

interface RowValidationErrorCardProps {
  errors: RowValidationError[];
  variant?: RowValidationVariant;
}

const getIdentifier = (err: RowValidationError): string => {
  if ('email' in err && err.email) return err.email;
  if ('name' in err && err.name) return err.name;
  if ('unitNumber' in err && err.unitNumber) return `Unit ${err.unitNumber}`;
  return '';
};

const VARIANT_CONFIG = {
  error: {
    title: (count: number) =>
      `${count} row${count !== 1 ? 's' : ''} ${count !== 1 ? 'have' : 'has'} issues that need to be fixed`,
    subtitle: 'Review the issues below, update your CSV, and re-upload',
    issueIcon: '✕',
  },
  warning: {
    title: (count: number) =>
      `${count} row${count !== 1 ? 's' : ''} ${count !== 1 ? 'have' : 'has'} optional fields with missing values`,
    subtitle:
      'These rows can still be imported, however blank optional fields will not overwrite existing data',
    issueIcon: '⚠',
  },
};

const RowValidationErrorCard = ({
  errors,
  variant = 'error',
}: RowValidationErrorCardProps) => {
  const config = VARIANT_CONFIG[variant];

  return (
    <div className={`row-val-card row-val-card_${variant}`}>
      <div className={`row-val-card-header row-val-card-header_${variant}`}>
        <div>
          <div className={`row-val-card-title row-val-card-title_${variant}`}>
            {config.title(errors.length)}
          </div>
          <div className={`row-val-card-subtitle row-val-card-subtitle_${variant}`}>
            {config.subtitle}
          </div>
        </div>
      </div>
      <div className="row-val-card-body">
        {errors.map((err: RowValidationError) => {
          const identifier: string = getIdentifier(err);

          return (
            <div
              key={err.row}
              className={`row-val-error-item row-val-error-item_${variant}`}
            >
              <div className="row-val-error-meta">
                <span className={`row-val-error-row row-val-error-row_${variant}`}>
                  Row {err.row}
                </span>
                {identifier && (
                  <span className="row-val-error-identifier">{identifier}</span>
                )}
              </div>
              <div className="row-val-error-issues">
                {err.issues.map((issue: string, i: number) => (
                  <span
                    key={i}
                    className={`row-val-error-issue row-val-error-issue_${variant}`}
                  >
                    {config.issueIcon} {issue}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RowValidationErrorCard;